import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Grid, Typography, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

import WrapperSection from '../../components/global/WrapperSection';
import GlobalBreadcrumbs from '../../components/global/GlobalBreadcrumbs';
import LoadingPage from '../../components/global/LoadingPage';

import FormAddEditNotificationTemplate from '../../components/notificationTemplates/FormAddEditNotificationTemplate';
import PreviewPane from '../../components/notificationTemplates/PreviewPane';
import TestSendDialog from '../../components/notificationTemplates/TestSendDialog';

import { useGetNotificationTemplateById } from '../../hooks/notificationTemplates/useGetNotificationTemplateById';
import { useUpdateNotificationTemplate } from '../../hooks/notificationTemplates/useUpdateNotificationTemplate';

const EditNotificationTemplate = () => {
    const { id } = useParams();
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { template, isLoading, error } = useGetNotificationTemplateById(id);
    const { updateTemplateAsync, isUpdating } = useUpdateNotificationTemplate();

    const [testOpen, setTestOpen] = useState(false);

    if (isLoading) return <LoadingPage />;
    if (error || !template) {
        return (
            <WrapperSection>
                <GlobalBreadcrumbs />
                <Alert severity="error">
                    {isArabic ? 'تعذر تحميل القالب' : 'Failed to load template'}
                </Alert>
            </WrapperSection>
        );
    }

    const handleSubmit = async (payload) => {
        try {
            await updateTemplateAsync({ id, data: payload });
            // stay on the page so the operator can keep iterating
        } catch {
            // toast handled by hook
        }
    };

    return (
        <WrapperSection>
            <GlobalBreadcrumbs />
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight={700}>
                    {isArabic ? 'تحرير قالب' : 'Edit template'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {template.code} · {template.channel}
                    {template.locale ? ` · ${template.locale.toUpperCase()}` : ''}
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                    <FormAddEditNotificationTemplate
                        mode="edit"
                        template={template}
                        onSubmit={handleSubmit}
                        isSubmitting={isUpdating}
                    />
                </Grid>
                <Grid item xs={12} md={5}>
                    <PreviewPane
                        templateId={template.id}
                        variables={template.variables}
                        channel={template.channel}
                        onTestSend={() => setTestOpen(true)}
                    />
                </Grid>
            </Grid>

            <TestSendDialog
                open={testOpen}
                onClose={() => setTestOpen(false)}
                templateId={template.id}
                variables={template.variables}
            />
        </WrapperSection>
    );
};

export default EditNotificationTemplate;
